import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import siteMetadata from '../utils/site-metadata'

function SEO({ graphqlData, title, description, path, schemas }) {
  const data = useMemo(() => {
    const defaultValues = { 
      title,
      description,
      code: '',
      meta: [], 
      links: []
    }
    const isNoIndex = graphqlData.nodes?.some((el) => el.name === 'robots' && el.value?.includes('noindex'));

    const graphql = graphqlData?.nodes?.reduce((prev, curr) => {
      switch (curr.name) {
        case "title":
        case "description":
          prev[curr.name] = curr.value;
        break;

        case "code":
          try {
            if (curr.value.length > 20 && JSON.parse(curr.value)) {
              schemas.push(curr.value);
            }
          } catch {}
        break;

        case "canonical":
          if (!isNoIndex) {
            prev.links.push({ rel: curr.name, href: curr.value });
          }
        break;

        case "keywords":
        case "robots": 
          prev.meta.push({ name: curr.name, content: curr.value });
        break;

        default:
          return prev;
      }

      return prev;
    }, defaultValues) || defaultValues;

    graphql.description = graphql?.description?.replace(/<[^>]*>|\[.*\]/g, '');
    graphql.title = `${graphql.title.split('|')[0]} | Ofin.pl`;

    if (graphql.title) {
      graphql.meta.push(
        { property: 'og:title', content: graphql.title },
        { name: 'twitter:title', content: graphql.title }
      );
    }

    if (graphql.description) {
      graphql.meta.push(
        { name: 'description', content: graphql.description },
        { property: 'og:description', content: graphql.description },
        { name: 'twitter:description', content: graphql.description }
      );
    }

    graphql.meta.push(
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `${siteMetadata.domain}${path}` },
      { property: 'og:locale', content: siteMetadata.lang },
      { name: 'twitter:card', content: 'summary' },
      { name: 'twitter:creator', content: siteMetadata.author }
    );

    if (!isNoIndex && graphql.meta.every((el) => el.name !== 'robots')) {
      graphql.meta.push({ name: 'robots', content: 'index,follow' })
    }

    if (!isNoIndex && graphql.links.every((el) => el.rel !== 'canonical')) {
      graphql.links.push({
        rel: 'canonical',
        href: `${siteMetadata.domain}${path}`
      })
    }

    return graphql;
  }, [graphqlData, title, description, path])

  return (<>
    {data.title && <title>{data.title}</title>}
    {data.links.map((props, i) => <link key={i} {...props} />)}
    {data.meta.map((props, i) => <meta key={i} {...props} />)}
    {schemas.map((el, i) => <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: el }} />)}
  </>)
}

SEO.defaultProps = {
  schemas: [],
  graphqlData: {},
  title: '',
  description: '',
  path: '',
}

SEO.propTypes = {
  schemas: PropTypes.array,
  graphqlData: PropTypes.object,
  title: PropTypes.string,
  description: PropTypes.string,
  path: PropTypes.string,
}

export default SEO

export const query = graphql`
  fragment SeoData on MysqlSeo {
    name
    value
  }
`;